import { captureException } from '@sentry/nextjs';

import type { BookingType } from '../hooks/use-booking-reducer';
import { isClassType } from '../hooks/use-booking-reducer';

// ─── Types ───────────────────────────────────────────────────────────

export interface CheckoutContext {
  // Booking selection
  bookingType: BookingType;
  date: Date;
  selectedTime: string;
  selectedDuration: string;
  selectedCourt: string;
  selectedTrainer: string;
  selectedBallMachine: string;
  selectedClass: string;
  isSelectedClassFull: boolean;
  bookingPrice: number;
  isPeakTime: boolean;
  templateId: string;
  numberOfParticipants: number;
  membershipId: string | null;
  selectedBookingAccount: { id: string; isChild: boolean } | null;

  // Payment
  paymentMethod: string;
  splitPayerCount: number;

  // User
  user: {
    id: string;
    email?: string;
    user_metadata?: { name?: string };
  } | null;
  guestUserId: string | null;
  guestEmail: string | null;
  guestName: string | null;
  guestPhone: string | null;

  // Display data
  courtName: string;
  className?: string;

  // Tenant
  tenantSlug: string;
  tenantPublishableKey: string | null;
  tenantAccountId: string | null;

  // Helpers
  getDurationFromLabel: (label: string) => number;
  calculateEndTime: (startTime: string, durationMinutes: number) => string;

  // Customer-facing add-ons
  selectedAddons?: Array<{ addonId: string; quantity: number; expectedUnitPrice?: number; bundleItemAddonIds?: string[] }>;

  // Phase B: auto-applied membership perk (resolved by the slot RPC).
  // When present, the booking-creation action inserts a court_booking_addons
  // discount row using these fields so the displayed final_price matches
  // what actually persists. Null when no perk applies to this slot.
  memberPerk?: { addonId: string; amountOff: number } | null;

  // Manual discount code previewed on the book page. The pending-booking
  // actions re-validate and redeem it server-side before any downstream
  // step (split payment init, payment-page checkout) reads booking_total.
  discountCode?: string | null;
}

export interface CheckoutCallbacks {
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onRedirect: (url: string) => void;
  onHyperpayCheckout: (data: unknown) => void;
  onMoyasarCheckout: (data: unknown) => void;
  onOpenGuestDrawer: () => void;
  onSetCreating: (creating: boolean) => void;
  onCreditsUpdated: (remaining: number) => void;
  onApplePayClick: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function buildCourtMetadata(ctx: CheckoutContext) {
  return {
    court_name: ctx.courtName,
    template_id: ctx.templateId,
    ...(ctx.membershipId && { membership_id: ctx.membershipId }),
    ...(ctx.bookingType === 'trainer' && {
      booking_type: 'trainer',
      trainer_id: ctx.selectedTrainer,
      ...(ctx.numberOfParticipants > 1 && {
        number_of_participants: String(ctx.numberOfParticipants),
      }),
    }),
    ...(ctx.bookingType === 'ball_machine' && {
      booking_type: 'ball_machine',
      trainer_id: ctx.selectedBallMachine,
      // Per-person pricing is not currently supported for ball_machine bookings,
      // but we include the participant count for consistency and future-proofing.
      ...(ctx.numberOfParticipants > 1 && {
        number_of_participants: String(ctx.numberOfParticipants),
      }),
    }),
  };
}

// ─── Class Checkout ──────────────────────────────────────────────────

async function handleClassPackageCheckout(
  ctx: CheckoutContext,
  cb: CheckoutCallbacks,
) {
  cb.onSetCreating(true);
  try {
    const { enrollInClassWithPackageAction } =
      await import('../server/class-enrollment/class-enrollment-with-package');
    const result = await enrollInClassWithPackageAction({
      bookingId: ctx.selectedClass,
      ...(ctx.selectedBookingAccount?.isChild && {
        bookingForUserId: ctx.selectedBookingAccount.id,
      }),
      addons: ctx.selectedAddons,
    });

    if (result.success && result.data) {
      cb.onSuccess('Enrolled using package credit!');
      cb.onCreditsUpdated(result.data.remainingCredits || 0);
      cb.onRedirect(`/payment/${result.data.participantId}?type=class`);
    } else {
      cb.onError(result.error || 'Failed to enroll in class');
    }
  } catch (error) {
    console.error('Error enrolling in class with package:', error);
    cb.onError('Failed to enroll in class. Please try again.');
  } finally {
    cb.onSetCreating(false);
  }
}

async function handleClassWaitlistJoin(
  ctx: CheckoutContext,
  cb: CheckoutCallbacks,
) {
  cb.onSetCreating(true);
  try {
    const { joinClassWaitlistAction } =
      await import('../server/class-waitlist-actions');
    const result = await joinClassWaitlistAction({
      bookingId: ctx.selectedClass,
      phoneNumber: ctx.guestPhone ?? undefined,
      guestUserId: ctx.guestUserId ?? undefined,
      bookingForUserId: ctx.selectedBookingAccount?.isChild
        ? ctx.selectedBookingAccount.id
        : undefined,
    });

    if (result.success) {
      cb.onSuccess('waitlistJoined');
    } else {
      cb.onError(result.error || 'Failed to join waitlist');
    }
  } catch (error) {
    console.error('Error joining class waitlist:', error);
    cb.onError('Failed to join waitlist. Please try again.');
  } finally {
    cb.onSetCreating(false);
  }
}

async function handleClassCardCheckout(
  ctx: CheckoutContext,
  cb: CheckoutCallbacks,
  guestUserIdOverride?: string,
  guestEmailOverride?: string,
  guestNameOverride?: string,
  guestPhoneOverride?: string,
) {
  cb.onSetCreating(true);
  try {
    const { createClassEnrollmentCheckoutAction } =
      await import('../server/class-enrollment-checkout-actions');

    const guestId = guestUserIdOverride || ctx.guestUserId;

    const result = await createClassEnrollmentCheckoutAction({
      bookingId: ctx.selectedClass,
      paymentMethod: ctx.paymentMethod === 'wallet' ? 'wallet' : 'card',
      guestUserId: guestId || undefined,
      ...(ctx.selectedBookingAccount?.isChild && {
        bookingForUserId: ctx.selectedBookingAccount.id,
      }),
      customerEmail: guestId
        ? guestEmailOverride || ctx.guestEmail || undefined
        : undefined,
      customerName: guestId
        ? guestNameOverride || ctx.guestName || 'Guest'
        : undefined,
      customerPhone: guestId
        ? guestPhoneOverride || ctx.guestPhone || undefined
        : undefined,
      returnUrl: `${window.location.origin}/book/return`,
      addons: ctx.selectedAddons,
      // Redeemed server-side right after enrollment (participant-scoped),
      // so the payment page's amountDue already reflects it on arrival.
      discountCode: ctx.discountCode ?? undefined,
    });

    if (!result.success || !result.data) {
      const resultError = 'error' in result ? result.error : undefined;
      cb.onError(resultError || 'Failed to enroll in class');
      cb.onSetCreating(false);
      return;
    }

    if ('usedWallet' in result.data && result.data.usedWallet) {
      cb.onSuccess('Class registration confirmed — paid from your wallet!');
      cb.onRedirect(`/payment/${result.data.participantId}?type=class`);
    } else {
      cb.onSuccess('Enrolled! Redirecting to payment...');
      cb.onRedirect(
        `/payment/${result.data.participantId}?type=class&autoOpen=card`,
      );
    }
    cb.onSetCreating(false);
  } catch (error) {
    console.error('Error enrolling in class:', error);
    cb.onError('Failed to enroll in class. Please try again.');
    cb.onSetCreating(false);
  }
}

// ─── Court/Trainer/BallMachine Checkout ──────────────────────────────

async function handleSplitPayment(ctx: CheckoutContext, cb: CheckoutCallbacks) {
  cb.onSetCreating(true);
  try {
    const formattedDate = formatDate(ctx.date);
    const duration = ctx.getDurationFromLabel(ctx.selectedDuration);
    const endTime = ctx.calculateEndTime(ctx.selectedTime, duration);

    const metadata = {
      ...buildCourtMetadata(ctx),
      split_payment: 'true',
      split_payer_count: ctx.splitPayerCount.toString(),
    };

    let bookingResult: {
      success: boolean;
      data?: { bookingId?: string };
      error?: string;
    };

    if (ctx.guestUserId) {
      // Guest split: create pending booking via guest action
      const { createPendingGuestCourtBookingAction } =
        await import('../server/court-booking/court-booking-actions-v2');

      bookingResult = await createPendingGuestCourtBookingAction({
        courtId: ctx.selectedCourt,
        bookingDate: formattedDate,
        startTime: ctx.selectedTime,
        endTime,
        slotDuration: duration,
        totalAmount: ctx.bookingPrice,
        customerEmail: ctx.guestEmail || '',
        customerPhone: ctx.guestPhone || '',
        customerName: ctx.guestName || 'Guest',
        guestUserId: ctx.guestUserId,
        isPeak: ctx.isPeakTime,
        metadata,
        addons: ctx.selectedAddons,
        memberPerk: ctx.memberPerk ?? undefined,
        discountCode: ctx.discountCode ?? undefined,
      });
    } else {
      // Authenticated split: create pending booking (no confirmation email).
      // Confirmation email is sent later when the first split payer completes payment.
      const { createPendingCourtBookingAction } =
        await import('../server/court-booking/court-booking-with-package');

      bookingResult = await createPendingCourtBookingAction({
        courtId: ctx.selectedCourt,
        bookingDate: formattedDate,
        startTime: ctx.selectedTime,
        endTime,
        slotDuration: duration,
        totalAmount: ctx.bookingPrice,
        customerEmail: ctx.user?.email || undefined,
        isPeak: ctx.isPeakTime,
        ...(ctx.selectedBookingAccount?.isChild && {
          bookingForUserId: ctx.selectedBookingAccount.id,
        }),
        metadata,
        addons: ctx.selectedAddons,
        memberPerk: ctx.memberPerk ?? undefined,
        discountCode: ctx.discountCode ?? undefined,
      });
    }

    if (!bookingResult.success || !bookingResult.data) {
      cb.onError(bookingResult.error || 'Failed to create booking');
      cb.onSetCreating(false);
      return;
    }

    const bookingId =
      'bookingId' in bookingResult.data ? bookingResult.data.bookingId : null;

    if (!bookingId) {
      cb.onError('Failed to get booking ID');
      cb.onSetCreating(false);
      return;
    }

    const { initializeSplitPaymentAction } =
      await import('../../../payment/_lib/server/split-payment-actions');

    const splitResult = await initializeSplitPaymentAction({
      bookingId,
      numberOfPayers: ctx.splitPayerCount,
    });

    if (!splitResult.success) {
      cb.onError(splitResult.error || 'Failed to initialize split payment');
      cb.onSetCreating(false);
      return;
    }

    cb.onSuccess('Split payment created! Redirecting to payment page...');
    cb.onRedirect(`/payment/${bookingId}`);
  } catch (error) {
    console.error('Error creating split payment:', error);
    cb.onError('Failed to create split payment');
  } finally {
    cb.onSetCreating(false);
  }
}

async function handleAuthenticatedCourtCheckout(
  ctx: CheckoutContext,
  cb: CheckoutCallbacks,
) {
  // Invariant: this function is only called when ctx.user is non-null.
  // Guard defensively so TypeScript non-null assertions below are safe.
  if (!ctx.user) {
    cb.onError('Authentication required. Please sign in and try again.');
    return;
  }

  cb.onSetCreating(true);
  try {
    const formattedDate = formatDate(ctx.date);
    const duration = ctx.getDurationFromLabel(ctx.selectedDuration);
    const endTime = ctx.calculateEndTime(ctx.selectedTime, duration);
    const metadata = buildCourtMetadata(ctx);

    if (ctx.paymentMethod === 'card') {
      const { createPendingCourtBookingAction } =
        await import('../server/court-booking/court-booking-with-package');

      const result = await createPendingCourtBookingAction({
        courtId: ctx.selectedCourt,
        bookingDate: formattedDate,
        startTime: ctx.selectedTime,
        endTime,
        slotDuration: duration,
        totalAmount: ctx.bookingPrice,
        customerEmail: ctx.user.email || undefined,
        isPeak: ctx.isPeakTime,
        ...(ctx.selectedBookingAccount?.isChild && {
          bookingForUserId: ctx.selectedBookingAccount.id,
        }),
        metadata,
        addons: ctx.selectedAddons,
        memberPerk: ctx.memberPerk ?? undefined,
        discountCode: ctx.discountCode ?? undefined,
      });

      if (result.success && result.data) {
        cb.onSuccess('Booking created! Redirecting to payment...');
        cb.onRedirect(`/payment/${result.data.bookingId}?autoOpen=card`);
      } else {
        cb.onError(
          result.error || 'Failed to create booking. Please try again.',
        );
      }
      cb.onSetCreating(false);
      return;
    }

    // Cash and package payments
    const { createCourtBookingWithPackageAction } =
      await import('../server/court-booking/court-booking-with-package');

    const result = await createCourtBookingWithPackageAction({
      courtId: ctx.selectedCourt,
      bookingDate: formattedDate,
      startTime: ctx.selectedTime,
      endTime,
      slotDuration: duration,
      totalAmount: ctx.bookingPrice,
      returnUrl: `${window.location.origin}/book/return`,
      customerEmail: ctx.user.email || undefined,
      isPeak: ctx.isPeakTime,
      ...(ctx.selectedBookingAccount?.isChild && {
        bookingForUserId: ctx.selectedBookingAccount.id,
      }),
      metadata,
      // Provider intentionally omitted — server resolves it from the tenant's
      // stored credentials. Don't let the client influence which gateway runs.
      usePackage: ctx.paymentMethod === 'package',
      useWallet: ctx.paymentMethod === 'wallet',
      paymentMethod: ctx.paymentMethod as
        | 'cash'
        | 'complimentary'
        | 'package'
        | 'card'
        | 'wallet',
      packageType: ctx.bookingType as 'court' | 'trainer',
      addons: ctx.selectedAddons,
      memberPerk: ctx.memberPerk ?? undefined,
      // Redeemed server-side inside the gateway-free branch so a 100%-off code
      // actually zeroes the booking instead of confirming it unpaid at full price.
      discountCode: ctx.discountCode ?? undefined,
    });

    if (result.success && result.data) {
      if (
        'paymentMethod' in result.data &&
        (result.data.paymentMethod === 'cash' ||
          result.data.paymentMethod === 'complimentary')
      ) {
        const isFree =
          'freeBooking' in result.data && result.data.freeBooking === true;
        cb.onSuccess(
          isFree
            ? 'Booking confirmed!'
            : 'Booking confirmed! Redirecting to payment page...',
        );
        cb.onRedirect(`/payment/${result.data.bookingId}`);
      } else if (
        'usedPackageCredit' in result.data &&
        result.data.usedPackageCredit
      ) {
        cb.onSuccess('Booking confirmed using package credit!');
        cb.onCreditsUpdated(result.data.remainingCredits || 0);
        cb.onRedirect(`/payment/${result.data.bookingId}`);
      } else if ('usedWallet' in result.data && result.data.usedWallet) {
        cb.onSuccess('Booking confirmed — paid from your wallet!');
        cb.onRedirect(`/payment/${result.data.bookingId}`);
      } else {
        cb.onError('Failed to process booking');
      }
    } else {
      cb.onError(
        result.error || 'Payment failed. Please contact us for assistance.',
      );
    }
  } catch (error) {
    console.error('Error creating checkout:', error);
    captureException(error);
    cb.onError('Payment failed. Please contact us for assistance.');
  } finally {
    cb.onSetCreating(false);
  }
}

async function handleGuestCourtCheckout(
  ctx: CheckoutContext,
  cb: CheckoutCallbacks,
  userId: string,
  email: string | undefined,
  name: string,
  phone: string,
) {
  cb.onSetCreating(true);
  try {
    const formattedDate = formatDate(ctx.date);
    const duration = ctx.getDurationFromLabel(ctx.selectedDuration);
    const endTime = ctx.calculateEndTime(ctx.selectedTime, duration);
    const metadata = {
      ...buildCourtMetadata(ctx),
      guest_name: name,
      guest_email: email || '',
      guest_phone: phone,
      ...(ctx.paymentMethod === 'split' && {
        split_payment: 'true',
        split_payer_count: ctx.splitPayerCount.toString(),
      }),
    };

    if (ctx.paymentMethod === 'card') {
      const { createPendingGuestCourtBookingAction } =
        await import('../server/court-booking/court-booking-actions-v2');

      const result = await createPendingGuestCourtBookingAction({
        courtId: ctx.selectedCourt,
        bookingDate: formattedDate,
        startTime: ctx.selectedTime,
        endTime,
        slotDuration: duration,
        totalAmount: ctx.bookingPrice,
        customerEmail: email || '',
        customerPhone: phone,
        customerName: name,
        guestUserId: userId,
        isPeak: ctx.isPeakTime,
        metadata,
        addons: ctx.selectedAddons,
        memberPerk: ctx.memberPerk ?? undefined,
        discountCode: ctx.discountCode ?? undefined,
      });

      if (result.success && result.data) {
        cb.onSuccess('Booking created! Redirecting to payment...');
        cb.onRedirect(`/payment/${result.data.bookingId}?autoOpen=card`);
      } else {
        cb.onError(
          result.error || 'Failed to create booking. Please try again.',
        );
      }
      cb.onSetCreating(false);
      return;
    }

    // Cash payments for guests (split is handled by handleSplitPayment)
    const { createGuestCourtBookingCheckoutActionV2 } =
      await import('../server/court-booking/court-booking-actions-v2');

    const result = await createGuestCourtBookingCheckoutActionV2({
      courtId: ctx.selectedCourt,
      bookingDate: formattedDate,
      startTime: ctx.selectedTime,
      endTime,
      slotDuration: duration,
      totalAmount: ctx.bookingPrice,
      returnUrl: `${window.location.origin}/book/return`,
      customerEmail: email || '',
      customerPhone: phone,
      customerName: name,
      guestUserId: userId,
      isPeak: ctx.isPeakTime,
      metadata,
      // Provider intentionally omitted — server resolves it from the tenant's
      // stored credentials. Don't let the client influence which gateway runs.
      paymentMethod:
        ctx.paymentMethod === 'applepay'
          ? 'card'
          : (ctx.paymentMethod as 'cash' | 'complimentary' | 'card'),
      addons: ctx.selectedAddons,
      memberPerk: ctx.memberPerk ?? undefined,
      // Redeemed server-side inside the gateway-free branch so a 100%-off code
      // actually zeroes the booking instead of confirming it unpaid at full price.
      discountCode: ctx.discountCode ?? undefined,
    });

    if (result.success && result.data) {
      // Gateway-free payment
      if (
        'paymentMethod' in result.data &&
        (result.data.paymentMethod === 'cash' ||
          result.data.paymentMethod === 'complimentary')
      ) {
        const isFree =
          'freeBooking' in result.data && result.data.freeBooking === true;
        cb.onSuccess(
          isFree
            ? 'Booking confirmed!'
            : 'Booking confirmed! Redirecting to payment page...',
        );
        const cashData = result.data as { bookingId: string };
        cb.onRedirect(`/payment/${cashData.bookingId}`);
      }
      // Provider-based checkout
      else if ('provider' in result.data && result.data.provider === 'tap') {
        if (result.data.redirectUrl) {
          window.location.href = result.data.redirectUrl;
        } else {
          cb.onError('Failed to initiate Tap payment');
        }
      } else if (
        'provider' in result.data &&
        result.data.provider === 'hyperpay'
      ) {
        cb.onHyperpayCheckout(result.data);
      } else if (
        'provider' in result.data &&
        result.data.provider === 'moyasar'
      ) {
        cb.onMoyasarCheckout(result.data);
      } else if ('url' in result.data && result.data.url) {
        window.location.href = result.data.url;
      } else {
        cb.onError('Failed to create checkout session');
      }
    } else {
      cb.onError(
        result.error || 'Payment failed. Please contact us for assistance.',
      );
    }
  } catch (error) {
    console.error('Error creating guest checkout:', error);
    captureException(error);
    cb.onError('Payment failed. Please contact us for assistance.');
  } finally {
    cb.onSetCreating(false);
  }
}

// ─── Main Dispatcher ─────────────────────────────────────────────────

export async function processCheckout(
  ctx: CheckoutContext,
  cb: CheckoutCallbacks,
) {
  // Waitlist flow — must be checked before any payment logic
  if (isClassType(ctx.bookingType) && ctx.isSelectedClassFull) {
    if (!ctx.date || !ctx.selectedClass) {
      cb.onError('Please select a class');
      return;
    }

    // Guest needs registration first
    if (!ctx.user && !ctx.guestUserId) {
      cb.onOpenGuestDrawer();
      return;
    }

    await handleClassWaitlistJoin(ctx, cb);
    return;
  }

  // Apple Pay for all booking types
  if (ctx.paymentMethod === 'applepay') {
    cb.onApplePayClick();
    return;
  }

  // Class bookings
  if (isClassType(ctx.bookingType)) {
    if (!ctx.date || !ctx.selectedClass || ctx.bookingPrice === null) {
      cb.onError('Please select a class');
      return;
    }

    // Guest needs registration first
    if (!ctx.user && !ctx.guestUserId) {
      cb.onOpenGuestDrawer();
      return;
    }

    // Package credit flow
    if (ctx.paymentMethod === 'package' && ctx.user) {
      await handleClassPackageCheckout(ctx, cb);
      return;
    }

    if (ctx.paymentMethod === 'wallet') {
      await handleClassCardCheckout(ctx, cb);
      return;
    }

    // Card flow
    await handleClassCardCheckout(ctx, cb);
    return;
  }

  // Court/trainer/ball_machine validation.
  // bookingPrice === 0 is a valid, complete selection (e.g. a complimentary
  // facility tour or a 100%-discount slot). Gate on `=== null` (price not yet
  // resolved) — matching the class path above — so a free booking isn't
  // rejected as "incomplete". Slot completeness is already enforced by the
  // date/time/duration/court checks.
  if (
    !ctx.date ||
    !ctx.selectedTime ||
    !ctx.selectedDuration ||
    !ctx.selectedCourt ||
    ctx.bookingPrice === null
  ) {
    cb.onError('Please complete all booking details');
    return;
  }

  // Split payment
  if (ctx.paymentMethod === 'split') {
    if (!ctx.user && !ctx.guestUserId) {
      cb.onOpenGuestDrawer();
      return;
    }
    await handleSplitPayment(ctx, cb);
    return;
  }

  // Authenticated user checkout
  if (ctx.user) {
    await handleAuthenticatedCourtCheckout(ctx, cb);
    return;
  }

  // Guest with existing registration
  if (ctx.guestUserId && ctx.guestPhone) {
    await handleGuestCourtCheckout(
      ctx,
      cb,
      ctx.guestUserId,
      ctx.guestEmail || undefined,
      ctx.guestName || '',
      ctx.guestPhone,
    );
    return;
  }

  // Need guest registration
  cb.onOpenGuestDrawer();
}

// Re-export for guest registration callback
export { handleGuestCourtCheckout, handleClassCardCheckout };
